/**
 * Shared API error handling utilities.
 *
 * Use `getApiErrorMessage` to turn an Axios error into a human-readable string,
 * and `handleApiError` to automatically call toast.error with the right message.
 *
 * 403  → "You don't have permission to <action>."
 * 404  → "The requested resource was not found."
 * 5xx  → backend detail if available, otherwise fallback
 * else → backend detail if available, otherwise fallback
 */

/**
 * Returns a user-friendly error message for an Axios error.
 *
 * @param {Error} error - The caught error (typically an Axios error).
 * @param {string} fallback - Message to show when no specific detail is available.
 * @param {string} [action] - Optional human-readable action name for 403 messages,
 *   e.g. "view inventory" → "You don't have permission to view inventory."
 * @returns {string}
 */
export const getApiErrorMessage = (error, fallback, action = '') => {
    const status = error?.response?.status;
    const data = error?.response?.data;

    // Handle quota exceeded errors (HTTP 429)
    if (status === 429 || data?.code === 'QUOTA_EXCEEDED') {
        return data?.error || 'Resource limit exceeded. Please try again later.';
    }

    // Handle feature disabled errors (HTTP 403 with FEATURE_DISABLED code)
    if (data?.code === 'FEATURE_DISABLED') {
        return data?.error || 'This feature is not available for your account.';
    }

    if (status === 403) {
        return action
            ? `You don't have permission to ${action}.`
            : "You don't have permission to perform this action.";
    }

    if (status === 404) {
        return data?.detail || data?.message || 'The requested resource was not found.';
    }

    // Backend-provided message takes priority over generic fallback
    const backendMsg =
        data?.detail ||
        data?.message ||
        data?.error ||
        (typeof data === 'string' ? data : null);

    if (backendMsg) {
        return backendMsg;
    }

    // DRF validation errors: { "field_name": ["error text"] } (no top-level detail)
    if (data && typeof data === 'object') {
        const skip = new Set(['detail', 'message', 'error', 'code']);
        const parts = [];
        for (const [key, val] of Object.entries(data)) {
            if (skip.has(key)) continue;
            if (Array.isArray(val)) {
                val.forEach((msg) => {
                    if (msg) parts.push(`${key}: ${msg}`);
                });
            } else if (val != null && val !== '') {
                parts.push(`${key}: ${val}`);
            }
        }
        if (parts.length > 0) {
            return parts.join(' ');
        }
    }

    return fallback;
};

/**
 * Calls toast.error with the resolved message.
 * Requires `toast` from 'react-toastify' to be passed in so this util stays
 * framework-agnostic and testable.
 *
 * @param {Function} toastError - e.g. `toast.error`
 * @param {Error} error
 * @param {string} fallback
 * @param {string} [action]
 */
export const toastApiError = (toastError, error, fallback, action = '') => {
    toastError(getApiErrorMessage(error, fallback, action));
};
